import { useEffect, useRef } from "react";
import { useTheme } from "../contexts/ThemeContext";
import "./CustomCursor.css";

const CustomCursor = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { theme } = useTheme();

  // Media queries evaluated ONCE at module scope (not inside component).
  // If user changes accessibility settings while page is open, cursor will NOT adapt.
  // A `change` listener on MediaQueryList would be needed for full responsiveness.
  // Known limitation.
  const isPointerCoarse = typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;
  const isReducedMotion = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (isPointerCoarse || isReducedMotion) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    // willReadFrequently: Performance hint for browsers. This canvas is frequently read
    // from (particle states examined each frame). Optimizes GPU/CPU for readback patterns.
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    // Signal to CSS that the custom cursor is active
    document.body.dataset.customCursorActive = "true";

    // Read the current theme accent color from CSS custom property ONCE at the start of the effect
    // to avoid layout reflows during the animation loop.
    const accentRgb = getComputedStyle(document.documentElement).getPropertyValue("--accent-rgb").trim() || "0, 240, 255";
    const accentColor = `rgb(${accentRgb})`;

    const getAccentRgb = () => accentRgb;
    const getAccentColor = () => accentColor;

    // -- Tooltip system (data-cursor-tooltip attribute driven) --
    // CRITICAL: Tooltips are managed via DOM manipulation (createElement, appendChild)
    // instead of React state because tooltip positions update at 60fps in the animation
    // loop. Using React state for 60 updates/sec would cause 60 re-renders per second,
    // destroying performance. The tooltip is an imperative escape hatch.

    let tooltipEl: HTMLDivElement | null = null;
    let tooltipVisible = false;
    let tooltipTimer: ReturnType<typeof setTimeout> | null = null;
    let lastTooltipElement: Element | null = null;
    let activeHoveredCard: HTMLElement | null = null;
    let scrollActiveTimeout: ReturnType<typeof setTimeout> | null = null;

    /** Create or retrieve the shared tooltip DOM element */
    const ensureTooltip = () => {
      if (tooltipEl) return tooltipEl;
      const el = document.createElement("div");
      el.className = "custom-cursor-tooltip";
      // z-index 2147483647: Maximum 32-bit signed integer (2^31 - 1), the highest
      // z-index browsers support. Ensures tooltip always renders on top.
      el.style.cssText = `
        position: fixed;
        pointer-events: none;
        z-index: 2147483647;
        max-width: 280px;
        padding: 8px 12px;
        background: rgba(var(--bg-secondary-rgb), 0.96);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        border: 1px solid rgba(var(--accent-rgb), 0.25);
        border-radius: 8px;
        color: var(--text-primary);
        font-size: 0.8rem;
        font-weight: 500;
        line-height: 1.4;
        box-shadow: 0 8px 24px rgba(0,0,0,0.35), 0 0 16px rgba(var(--accent-rgb), 0.15);
        transform: translate(-50%, -100%);
        opacity: 0;
        transition: opacity 0.15s ease, transform 0.15s ease;
        font-family: var(--font-main);
        white-space: nowrap;
      `;
      document.body.appendChild(el);
      tooltipEl = el;
      return el;
    };

    /** Show tooltip at a specific viewport coordinate */
    const showTooltip = (text: string, x: number, y: number) => {
      if (!text) return;
      if (tooltipTimer) {
        clearTimeout(tooltipTimer);
        tooltipTimer = null;
      }
      tooltipVisible = true;
      const el = ensureTooltip();
      el.textContent = text;
      el.style.left = `${x}px`;
      el.style.top = `${y - 12}px`;
      el.style.opacity = "1";
      el.style.transform = "translate(-50%, -100%)";
    };

    /** Show tooltip for an element by reading its data-cursor-tooltip attribute and positioning at its center */
    const showTooltipAtElement = (el: Element) => {
      lastTooltipElement = el;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top;
      showTooltip(el.getAttribute("data-cursor-tooltip") || "", cx, cy);
    };

    /** Hide the tooltip with a small downward translate animation */
    const hideTooltip = () => {
      if (!tooltipEl || !tooltipVisible) return;
      tooltipVisible = false;
      lastTooltipElement = null;
      tooltipEl.style.opacity = "0";
      tooltipEl.style.transform = "translate(-50%, calc(-100% + 8px))";
    };

    /** Schedule tooltip hide after a brief delay (used on mouseout to avoid flickering) */
    const scheduleHide = () => {
      if (tooltipTimer) clearTimeout(tooltipTimer);
      tooltipTimer = setTimeout(() => {
        hideTooltip();
        tooltipTimer = null;
      }, 120);
    };

    // -- Canvas dimensions --
    let w = (canvas.width = window.innerWidth);
    let h = (canvas.height = window.innerHeight);

    // -- Pointer state --
    const mouse = { x: 0, y: 0, active: false };
    const mouseDownPos = { x: 0, y: 0 };
    let isHovering = false;
    let isMouseDown = false;
    let isTouchInput = false;
    let lastTouchTime = 0;
    let currentDotSize = 8;
    let isDraggable = false;

    // -- Particle containers --
    // -- Trail system: capped at 25 points to prevent unbounded memory growth.
    // Older points are shifted out (FIFO). Life decay of 0.04 per frame gives
    // approximately 25 frames of visible trail (~400ms at 60fps).
    let sparks: Spark[] = [];
    let ripples: Ripple[] = [];
    let smoke: SmokeParticle[] = [];
    let trailPoints: { x: number; y: number; life: number }[] = [];

    /** Add a point to the cursor trail (capped at 25 points) */
    // Trail capped at 25 points to prevent unbounded memory growth. Older points
    // shifted out (FIFO). Life decay 0.04/frame gives ~25 frames (~400ms at 60fps).
    const addTrailPoint = (x: number, y: number) => {
      trailPoints.push({ x, y, life: 1.0 });
      // Cap trail points history size
      if (trailPoints.length > 25) {
        trailPoints.shift();
      }
    };

    /**
     * A small particle that bursts outward from a click point.
     * Each particle class has its own physics: sparks explode outward with
     * drag (0.93), ripples expand and fade, smoke floats upward with buoyancy.
     * The magic numbers (speed 2-6.5, decay 0.02-0.05, drag 0.93) are visually
     * tuned for each effect.
     * Each spark has a random direction, speed, color, and decay rate.
     */
    class Spark {
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      color: string;
      life: number;
      decay: number;
      constructor(x: number, y: number) {
        this.x = x;
        this.y = y;
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 4.5 + 2.0;
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed;
        this.size = Math.random() * 2 + 1.2;
        this.life = 1.0;
        this.decay = Math.random() * 0.03 + 0.02;
        const colors = [getAccentColor(), "#a78bfa", "#ffffff"];
        this.color = colors[Math.floor(Math.random() * colors.length)];
      }
      update() {
        this.x += this.vx;
        this.y += this.vy;
        this.vx *= 0.93; // drag
        this.vy *= 0.93;
        this.life -= this.decay;
      }
      draw() {
        if (!ctx) return;
        const radius = this.size * this.life;
        if (radius <= 0) return;
        ctx.beginPath();
        ctx.arc(this.x, this.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.fill();
      }
    }

    /**
     * A circular ripple that expands outward from a click point and fades.
     * Rendered as a thin accent-colored stroke.
     */
    class Ripple {
      x: number;
      y: number;
      radius: number;
      maxRadius: number;
      opacity: number;
      decay: number;
      constructor(x: number, y: number) {
        this.x = x;
        this.y = y;
        this.radius = 0;
        this.maxRadius = 55;
        this.opacity = 1.0;
        this.decay = 0.035;
      }
      update() {
        this.radius += (this.maxRadius - this.radius) * 0.12;
        this.opacity -= this.decay;
      }
      draw() {
        if (!ctx) return;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${getAccentRgb()}, ${Math.max(0, this.opacity)})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
    }

    /**
     * A small particle that drifts upward like smoke from the cursor path.
     * Has a subtle upward buoyancy acceleration and rapid fade.
     */
    class SmokeParticle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      color: string;
      life: number;
      decay: number;
      constructor(x: number, y: number) {
        this.x = x;
        this.y = y;
        this.vx = (Math.random() - 0.5) * 1.6;
        this.vy = -Math.random() * 1.5 - 0.5; // upward drift
        this.size = Math.random() * 2.5 + 2.0;
        this.life = 1.0;
        this.decay = Math.random() * 0.035 + 0.025; // fades rapidly (30-40 frames)
        const colors = [
          `rgba(${getAccentRgb()}, 0.45)`,
          "rgba(167, 139, 250, 0.45)", // Violet
          "rgba(59, 130, 246, 0.45)", // Blue
        ];
        this.color = colors[Math.floor(Math.random() * colors.length)];
      }
      update() {
        this.x += this.vx;
        this.y += this.vy;
        this.vx *= 0.96;
        this.vy += -0.06; // upward buoyancy acceleration
        this.life -= this.decay;
      }
      draw() {
        if (!ctx) return;
        const radius = this.size * this.life;
        if (radius <= 0) return;
        ctx.beginPath();
        ctx.arc(this.x, this.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.fill();
      }
    }

    /** Trigger a click burst: one expanding ripple and 8 sparks that scatter outward */
    // 8 sparks + 1 ripple per click provides a satisfying burst without overwhelming the canvas.
    // The 8/1 split was visually tuned -- more sparks crowd the canvas, fewer feels weak.
    const triggerBlast = (x: number, y: number) => {
      ripples.push(new Ripple(x, y));
      for (let i = 0; i < 8; i++) {
        sparks.push(new Spark(x, y));
      }
    };

    // -- Mouse event handlers --
    /** Track cursor position, spawn smoke particles, and record trail points */
    const handleMouseMove = (e: MouseEvent) => {
      isTouchInput = false;
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      mouse.active = true;

      // Only add trail and smoke for real user mouse moves, not programmatic scroll-triggered moves
      if (e.isTrusted) {
        addTrailPoint(e.clientX, e.clientY);

        // Spawn 1 smoke particle per move event (throttled by max count)
        if (smoke.length < 25) {
          smoke.push(new SmokeParticle(e.clientX, e.clientY));
        }
      }
    };

    /** Hide cursor visuals and tooltip when mouse leaves the window */
    const handleMouseLeave = () => {
      mouse.active = false;
      scheduleHide();
      if (activeHoveredCard) {
        activeHoveredCard.classList.remove("hovered");
        activeHoveredCard = null;
      }
    };

    /** Record mouse-down position for click detection (avoid drag-triggered bursts) */
    // Touch guard 500ms: Prevents click events from firing immediately after touch events.
    // 500ms is the standard mobile tap delay. Without this, touch devices would double-fire
    // (touch + click) on taps.
    const handleMouseDown = (e: MouseEvent) => {
      if (Date.now() - lastTouchTime < 500) return;
      isTouchInput = false;
      isMouseDown = true;
      mouseDownPos.x = e.clientX;
      mouseDownPos.y = e.clientY;
    };

    const handleMouseUp = () => {
      isMouseDown = false;
    };

    /** Trigger visual blast on click if the pointer didn't drag more than 6px */
    // Drag distance 6px: Threshold for distinguishing click vs drag on touch/mouse swipe.
    // If pointer moved more than 6px, it is a drag, not a click.
    // Click bursts only fire on stationary clicks.
    const handleClick = (e: MouseEvent) => {
      if (e.clientX === 0 && e.clientY === 0) return;

      const dx = e.clientX - mouseDownPos.x;
      const dy = e.clientY - mouseDownPos.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Only trigger click burst if the pointer did not drag more than 6 pixels
      if (distance < 6) {
        triggerBlast(e.clientX, e.clientY);
      }
    };

    const handleScroll = () => {
      if (lastTooltipElement && tooltipVisible && tooltipEl) {
        const rect = lastTooltipElement.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top;
        tooltipEl.style.left = `${cx}px`;
        tooltipEl.style.top = `${cy - 12}px`;
      } else {
        hideTooltip();
      }

      // Throttle layout queries and synthetic events to 16ms (~60fps) on scroll
      // to ensure the browser has updated element positions and avoid performance degradation.
      if (!scrollActiveTimeout) {
        scrollActiveTimeout = setTimeout(() => {
          scrollActiveTimeout = null;
          if (mouse.active) {
            const target = document.elementFromPoint(mouse.x, mouse.y) as HTMLElement | null;
            if (target) {
              updateHoverState(target);
              
              target.dispatchEvent(
                new MouseEvent("mousemove", {
                  clientX: mouse.x,
                  clientY: mouse.y,
                  bubbles: true,
                  cancelable: true,
                })
              );
              target.dispatchEvent(
                new MouseEvent("mouseover", {
                  clientX: mouse.x,
                  clientY: mouse.y,
                  bubbles: true,
                  cancelable: true,
                })
              );
            }
          }
        }, 16);
      }
    };

    const updateHoverState = (target: HTMLElement) => {
      const cardTarget = target.closest(".milestone-card, .achievement-card") as HTMLElement | null;
      if (cardTarget) {
        if (cardTarget !== activeHoveredCard) {
          if (activeHoveredCard) {
            activeHoveredCard.classList.remove("hovered");
          }
          activeHoveredCard = cardTarget;
          cardTarget.classList.add("hovered");
        }
      } else {
        if (activeHoveredCard) {
          activeHoveredCard.classList.remove("hovered");
          activeHoveredCard = null;
        }
      }
    };

    /** Determine hover/clickable/draggable state and show/hide tooltip when mouse enters an element */
    const handleMouseOver = (e: MouseEvent) => {
      isTouchInput = false;
      const target = e.target as HTMLElement;
      if (!target) return;

      updateHoverState(target);

      const tooltipTarget = target.closest("[data-cursor-tooltip]");
      if (tooltipTarget) {
        const text = tooltipTarget.getAttribute("data-cursor-tooltip") || "";
        if (text) {
          showTooltipAtElement(tooltipTarget);
        }
      } else {
        scheduleHide();
      }

      // Hover detection multi-strategy: Uses tagName checks (A, BUTTON),
      // closest() searches, class checks (interactive, dev-comment), and
      // getComputedStyle. This handles edge cases (custom elements, elements
      // with CSS cursor:pointer). getComputedStyle is last because it triggers
      // style recalc, which is expensive.
      const isInsideAchievement =
        !!target.closest(".achievement-card") ||
        !!target.closest(".milestone-card");
      const isClickable =
        target.tagName === "A" ||
        target.tagName === "BUTTON" ||
        target.tagName === "INPUT" ||
        target.tagName === "SELECT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "LABEL" ||
        target.tagName === "SUMMARY" ||
        target.closest("a") ||
        target.closest("button") ||
        target.closest('[role="button"]') ||
        target.closest('[role="link"]') ||
        target.closest('[role="checkbox"]') ||
        target.closest('[role="menuitem"]') ||
        target.closest('[role="tab"]') ||
        target.classList.contains("interactive") ||
        target.classList.contains("dev-badge") ||
        !!target.closest(".interactive") ||
        !!target.closest(".dev-comment-badge, .dev-comment-wrapper, .dev-comment-popup-close, .dev-terminal-minimized-content") ||
        !!target.closest(".faq-question, .faq-header, .accordion-header, .accordion-trigger") ||
        !!target.closest(".nav-link, .nav-item, .theme-toggle, .mobile-menu-btn") ||
        !!target.closest(".btn, .button") ||
        !!target.closest(".github-card, .project-card, .project-link, .github-link") ||
        !!target.closest(".rec-nav-btn, .rec-dot, .author-profile-link") ||
        target.style.cursor === "pointer";

      if (!isInsideAchievement && isClickable) {
        isHovering = true;
      } else {
        isHovering = false;
      }

      // Detect draggable elements (cert-stack-card, rec-card) for drag chevrons
      if (
        !isInsideAchievement &&
        !isClickable &&
        (target.classList.contains("cert-stack-card") ||
          target.closest(".cert-stack-card") ||
          target.classList.contains("rec-card") ||
          target.closest(".rec-card"))
      ) {
        isDraggable = true;
      } else {
        isDraggable = false;
      }
    };

    // -- Mobile touch handlers --
    const handleTouchStart = (e: TouchEvent) => {
      isTouchInput = true;
      lastTouchTime = Date.now();
      mouse.active = true;
      if (e.touches.length > 0) {
        const x = e.touches[0].clientX;
        const y = e.touches[0].clientY;
        mouse.x = x;
        mouse.y = y;
        mouseDownPos.x = x;
        mouseDownPos.y = y;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      isTouchInput = true;
      if (e.touches.length > 0) {
        mouse.active = true;
        const x = e.touches[0].clientX;
        const y = e.touches[0].clientY;
        mouse.x = x;
        mouse.y = y;
        addTrailPoint(x, y);

        // Spawn 1 smoke particle on touch drag (throttled by max count)
        if (smoke.length < 25) {
          smoke.push(new SmokeParticle(x, y));
        }
      }
    };

    const handleTouchEnd = () => {
      mouse.active = false;
    };

    // ALL event listeners use capture phase (true). This is CRITICAL: capture phase
    // ensures the cursor canvas handles events before child elements. Without capture,
    // clickable elements inside the page would intercept mouse events before the cursor
    // animation sees them, causing cursor lag.
    window.addEventListener("mousemove", handleMouseMove, true);
    window.addEventListener("mouseout", handleMouseLeave, true);
    window.addEventListener("mousedown", handleMouseDown, true);
    window.addEventListener("mouseup", handleMouseUp, true);
    window.addEventListener("click", handleClick, true);
    window.addEventListener("mouseover", handleMouseOver, true);
    window.addEventListener("wheel", handleScroll, { capture: true, passive: true });
    window.addEventListener("scroll", handleScroll, { capture: true, passive: true });
    window.addEventListener("touchstart", handleTouchStart, { capture: true, passive: true });
    window.addEventListener("touchmove", handleTouchMove, { capture: true, passive: true });
    window.addEventListener("touchend", handleTouchEnd, { capture: true, passive: true });

    /** Keep canvas dimensions in sync with viewport on resize */
    const handleResize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", handleResize);

    // -- Main animation loop --
    let animationFrameId = 0;
    const animate = () => {
      ctx.clearRect(0, 0, w, h);

      // 1. Trail line: neon glow layer + white core layer (two-pass rendering)
      // First pass: thick neon glow (width 10*ratio, opacity 0.18*ratio).
      // Second pass: thin white core (width 2.5*ratio, opacity 0.85*ratio).
      // The ratio-based taper creates a smooth trail that fades at both ends.
      // Values (10, 0.18, 2.5, 0.85) are visually tuned.
      for (let i = 0; i < trailPoints.length; i++) {
        trailPoints[i].life -= 0.04;
      }
      trailPoints = trailPoints.filter((p) => p.life > 0);

      if (trailPoints.length > 1) {
        // 1a. Thick neon glow layer (translucent accent-colored trail)
        for (let i = 1; i < trailPoints.length; i++) {
          const p1 = trailPoints[i - 1];
          const p2 = trailPoints[i];
          const ratio = i / trailPoints.length;

          ctx.beginPath();
          ctx.lineWidth = ratio * 10;
          ctx.strokeStyle = `rgba(${getAccentRgb()}, ${ratio * 0.18})`;
          ctx.lineCap = "round";
          ctx.lineJoin = "round";
          ctx.moveTo(p1.x, p1.y);
          ctx.lineTo(p2.x, p2.y);
          ctx.stroke();
        }

        // 1b. Thin sharp white core layer (bright inner trail)
        for (let i = 1; i < trailPoints.length; i++) {
          const p1 = trailPoints[i - 1];
          const p2 = trailPoints[i];
          const ratio = i / trailPoints.length;

          ctx.beginPath();
          ctx.lineWidth = ratio * 2.5;
          ctx.strokeStyle = `rgba(255, 255, 255, ${ratio * 0.85})`;
          ctx.lineCap = "round";
          ctx.lineJoin = "round";
          ctx.moveTo(p1.x, p1.y);
          ctx.lineTo(p2.x, p2.y);
          ctx.stroke();
        }
      }

      // 2. Floating smoke particles (drift upward from cursor path)
      smoke = smoke.filter((p) => p.life > 0);
      for (let i = 0; i < smoke.length; i++) {
        smoke[i].update();
        smoke[i].draw();
      }

      // 3. Click ripples (expanding circles on click)
      ripples = ripples.filter((r) => r.opacity > 0);
      for (let i = 0; i < ripples.length; i++) {
        ripples[i].update();
        ripples[i].draw();
      }

      // 4. Click sparks (small colored particles bursting outward on click)
      sparks = sparks.filter((s) => s.life > 0);
      for (let i = 0; i < sparks.length; i++) {
        sparks[i].update();
        sparks[i].draw();
      }

      // 5. Desktop cursor: inner dot, hand pointer on hover, drag chevrons on draggable (only non-touch)
      if (!isTouchInput && mouse.active) {
        const targetDotSize = isHovering || isDraggable ? 0 : isMouseDown ? 5 : 8;

        currentDotSize += (targetDotSize - currentDotSize) * 0.15;

        // 5a. Inner dot: shrinks on hover/mousedown, animated via smoothing
        if (currentDotSize > 0.05) {
          ctx.beginPath();
          ctx.arc(mouse.x, mouse.y, currentDotSize / 2, 0, Math.PI * 2);
          ctx.fillStyle = getAccentColor();
          ctx.fill();
        }

        // 5b. Hand pointer icon on hover, rendered as an SVG path from FontAwesome with neon glow
        // The FontAwesome hand-pointer SVG path is rendered on canvas with transformations:
        // rotate -0.26 rad (~ -15 degrees), scale 0.048 (512px to ~24px), translate -144
        // (align index finger tip to cursor point). The shadow blur division (10/scale) is
        // needed because canvas shadow blur operates in pre-transform space, not post-transform.
        if (isHovering && !isMouseDown) {
          ctx.save();

          // Translate to mouse position so the index finger tip is exactly at the cursor coordinate
          ctx.translate(mouse.x, mouse.y);

          // Classically tilt the hand pointer cursor by -15 degrees (-0.26 radians)
          ctx.rotate(-0.26);

          const scale = 0.048; // Scale 512px SVG path down to ~24px height
          ctx.scale(scale, scale);

          // Align the tip of the index finger (approx x=144, y=0 in FontAwesome coordinates) to the cursor point
          ctx.translate(-144, 0);

          // Setup canvas shadow for bright neon glow
          ctx.shadowColor = `rgba(${getAccentRgb()}, 0.85)`;
          ctx.shadowBlur = 10 / scale; // Adjust blur for scaled canvas

          // Standard FontAwesome 5 Solid hand-pointer SVG path data
          const handPath = new Path2D(
            "M448 240v96c0 3.084-.356 6.159-1.063 9.162l-32 136C410.686 499.23 394.562 512 376 512H168a40.004 40.004 0 0 1-32.35-16.473l-127.997-176c-12.993-17.866-9.043-42.883 8.822-55.876 17.867-12.994 42.884-9.043 55.877 8.823L104 315.992V40c0-22.091 17.908-40 40-40s40 17.909 40 40v200h8v-40c0-22.091 17.908-40 40-40s40 17.909 40 40v40h8v-24c0-22.091 17.908-40 40-40s40 17.909 40 40v24h8c0-22.091 17.908-40 40-40s40 17.909 40 40z",
          );

          // Stroke with neon accent color
          ctx.strokeStyle = getAccentColor();
          ctx.lineWidth = 1.8 / scale; // Renders as exactly 1.8px stroke width on screen
          ctx.lineCap = "round";
          ctx.lineJoin = "round";
          ctx.stroke(handPath);

          // Reset shadow before filling to keep the fill clean
          ctx.shadowBlur = 0;

          // Fill with solid bright white for maximum visibility on dark themed background
          ctx.fillStyle = "#ffffff";
          ctx.fill(handPath);

          ctx.restore();
        }

        // 5c. Drag chevrons (left/right arrows) on draggable elements; pull inward while dragging
        if (isDraggable) {
          ctx.save();
          ctx.translate(mouse.x, mouse.y);

          // If the user is dragging (isMouseDown is true), make the chevrons pull inwards!
          const offset = isMouseDown ? 15 : 25;
          const size = 6;

          ctx.strokeStyle = getAccentColor();
          ctx.lineWidth = 2;
          ctx.lineCap = "round";
          ctx.lineJoin = "round";

          // Setup glow
          ctx.shadowColor = getAccentColor();
          ctx.shadowBlur = 8;

          // Left chevron ◀
          ctx.beginPath();
          ctx.moveTo(-offset + size, -size);
          ctx.lineTo(-offset, 0);
          ctx.lineTo(-offset + size, size);
          ctx.stroke();

          // Right chevron ▶
          ctx.beginPath();
          ctx.moveTo(offset - size, -size);
          ctx.lineTo(offset, 0);
          ctx.lineTo(offset - size, size);
          ctx.stroke();

          // Center dot (smaller and white, representing the grab point)
          ctx.beginPath();
          ctx.arc(0, 0, isMouseDown ? 2 : 4, 0, Math.PI * 2);
          ctx.fillStyle = "#ffffff";
          ctx.shadowBlur = 0; // no shadow for the dot
          ctx.fill();

          ctx.restore();
        }
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    // -- Cleanup on unmount: remove all listeners, hide tooltip, stop animation --
    // KNOWN ISSUE: When theme changes, the ENTIRE canvas + tooltip system is torn down
    // and recreated. All particle state (sparks, ripples, smoke, trail) is lost, causing
    // a visible flash/pop in cursor effects. A more efficient approach would update only
    // the accent color without destroying state. If fixing, extract accent color reading
    // outside the effect and only reinitialize the color reference, not the full system.
    return () => {
      window.removeEventListener("mousemove", handleMouseMove, true);
      window.removeEventListener("mouseout", handleMouseLeave, true);
      window.removeEventListener("mousedown", handleMouseDown, true);
      window.removeEventListener("mouseup", handleMouseUp, true);
      window.removeEventListener("click", handleClick, true);
      window.removeEventListener("mouseover", handleMouseOver, true);
      window.removeEventListener("wheel", handleScroll, { capture: true });
      window.removeEventListener("scroll", handleScroll, { capture: true });
      window.removeEventListener("touchstart", handleTouchStart, { capture: true });
      window.removeEventListener("touchmove", handleTouchMove, { capture: true });
      window.removeEventListener("touchend", handleTouchEnd, { capture: true });
      window.removeEventListener("resize", handleResize);
      delete document.body.dataset.customCursorActive;
      cancelAnimationFrame(animationFrameId);
      if (scrollActiveTimeout) {
        clearTimeout(scrollActiveTimeout);
      }
    };
  }, [theme, isPointerCoarse, isReducedMotion]);

  // Render nothing for touch-only or reduced-motion users
  if (isPointerCoarse || isReducedMotion) return null;

  return <canvas ref={canvasRef} className="custom-cursor-canvas" />;
};

export default CustomCursor;
